// `agent_events` readers: one agent's activity log (the "Open log" view) and a
// live feed of handoffs that sends couriers walking across the world map.

import { useEffect, useRef, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";

const LOG_LIMIT = 30;
const millis = (ts) => ts?.toMillis?.() ?? 0;

/** Newest-first events for one agent, live. */
export function useAgentLog(db, agentId) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db || !agentId) return undefined;
    setLoading(true);
    const map = (snap) =>
      snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => millis(b.at) - millis(a.at)).slice(0, LOG_LIMIT);

    let unsub = onSnapshot(
      query(collection(db, "agent_events"), where("agentId", "==", agentId), orderBy("at", "desc"), limit(LOG_LIMIT)),
      (snap) => {
        setEvents(map(snap));
        setLoading(false);
      },
      () => {
        // agent_events (agentId, at desc) index not deployed yet — sort client-side
        unsub = onSnapshot(query(collection(db, "agent_events"), where("agentId", "==", agentId)), (snap) => {
          setEvents(map(snap));
          setLoading(false);
        });
      }
    );
    return () => unsub();
  }, [db, agentId]);

  return { events, loading };
}

/**
 * Calls onHandoff({ from, to, item, message }) for each handoff event logged
 * while the page is open (history is skipped — couriers are for live work).
 */
export function useHandoffFeed(db, onHandoff) {
  const cb = useRef(onHandoff);
  cb.current = onHandoff;

  useEffect(() => {
    if (!db) return undefined;
    let primed = false;
    return onSnapshot(query(collection(db, "agent_events"), orderBy("at", "desc"), limit(20)), (snap) => {
      if (primed) {
        snap.docChanges().forEach((c) => {
          const v = c.doc.data();
          if (c.type === "added" && v.type === "handoff" && v.toAgentId) {
            cb.current?.({ from: v.agentId, to: v.toAgentId, item: v.item, message: v.message });
          }
        });
      }
      primed = true;
    });
  }, [db]);
}
